import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, forceUpdate, Host, Listen, Method, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, Conference, Notetaker, ThemeConfig } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

/**
 * Interface representing the complete state of the notetaker configuration
 */
export interface NotetakerState {
  enabled: boolean;
  notetakerName: string;
  showUiConsentMessage: boolean;
  videoRecording: boolean;
  audioRecording: boolean;
  transcription: boolean;
  summary: boolean;
  actionItems: boolean;
  summaryCustomInstructions: string;
  actionItemsCustomInstructions: string;
  isConferencingConfigured: boolean;
  conferencing?: Conference;
  validationError?: string;
  summaryInstructionsError?: string;
  actionItemsInstructionsError?: string;
}

/**
 * The `nylas-notetaker-config` component is a UI component that allows users to configure Notetaker settings for scheduler configurations.
 * @part nntc - The notetaker config container
 * @part nntc__header - The header of the notetaker config
 * @part nntc__section - A section container
 * @part nntc__section-title - A section title
 * @part nntc__content - The content container
 * @part nntc__input - An input field
 * @part nntc__textarea - A textarea field
 * @part nntc__checkbox - A checkbox container
 */
@Component({
  tag: 'nylas-notetaker-config',
  styleUrl: 'nylas-notetaker-config.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasNotetakerConfig {
  @Element() host!: HTMLNylasNotetakerConfigElement;

  /**
   * @standalone
   * The selected configuration
   */
  @Prop() selectedConfiguration?: Configuration;

  /**
   * @standalone
   * The conferencing configuration
   */
  @Prop() conferencing?: Conference;

  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'notetaker';

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * State for enabled/disabled
   */
  @State() enabled: boolean = false;

  /**
   * State for show UI consent message
   */
  @State() showUiConsentMessage: boolean = false;

  /**
   * State for notetaker name
   */
  @State() notetakerName: string = '';

  /**
   * State for video recording
   */
  @State() videoRecording: boolean = false;

  /**
   * State for audio recording
   */
  @State() audioRecording: boolean = false;

  /**
   * State for transcription
   */
  @State() transcription: boolean = false;

  /**
   * State for AI summary
   */
  @State() summary: boolean = false;

  /**
   * State for AI summary custom instructions
   */
  @State() summaryCustomInstructions: string = '';

  /**
   * State for AI action items
   */
  @State() actionItems: boolean = false;

  /**
   * State for AI action items custom instructions
   */
  @State() actionItemsCustomInstructions: string = '';

  /**
   * State for validation error
   */
  @State() validationError: string = '';

  /**
   * State for summary custom instructions error
   */
  @State() summaryInstructionsError: string = '';

  /**
   * State for action items custom instructions error
   */
  @State() actionItemsInstructionsError: string = '';

  /**
   * Maximum character limit for custom instructions
   */
  private readonly MAX_CUSTOM_INSTRUCTIONS_LENGTH = 1500;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * This event is fired when the notetaker configuration changes.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * This event is fired when an error occurs in the notetaker configuration.
   */
  @Event() notetakerError!: EventEmitter<{
    error: string;
    reason: string;
    context?: Record<string, any>;
  }>;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-notetaker-config', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration) {
    debug('nylas-notetaker-config', 'selectedConfigurationChangedHandler', newValue);
    const notetaker = newValue?.scheduler?.notetaker_settings;
    if (notetaker) {
      this.enabled = notetaker.enabled ?? false;
      this.showUiConsentMessage = notetaker.show_ui_consent_message ?? false;
      this.notetakerName = notetaker.notetaker_name ?? '';
      this.videoRecording = notetaker.meeting_settings?.video_recording ?? false;
      this.audioRecording = notetaker.meeting_settings?.audio_recording ?? false;
      this.transcription = notetaker.meeting_settings?.transcription ?? false;
      this.summary = notetaker.meeting_settings?.summary ?? false;
      this.summaryCustomInstructions = notetaker.meeting_settings?.summary_settings?.custom_instructions ?? '';
      this.actionItems = notetaker.meeting_settings?.action_items ?? false;
      this.actionItemsCustomInstructions = notetaker.meeting_settings?.action_items_settings?.custom_instructions ?? '';
    } else {
      // Reset to defaults
      this.enabled = false;
      this.showUiConsentMessage = false;
      this.notetakerName = '';
      this.videoRecording = false;
      this.audioRecording = false;
      this.transcription = false;
      this.summary = false;
      this.summaryCustomInstructions = '';
      this.actionItems = false;
      this.actionItemsCustomInstructions = '';
    }
    this.validateAndEmit(false);
  }

  @Watch('conferencing')
  conferencingChangedHandler(newValue: Conference | undefined, oldValue?: Conference | undefined) {
    debug('nylas-notetaker-config', 'conferencingChangedHandler', { newValue, oldValue });

    const wasConfigured = !!(oldValue && oldValue.provider);
    const isConfigured = !!(newValue && newValue.provider);

    // Log state transitions for debugging
    if (!wasConfigured && isConfigured) {
      debug('nylas-notetaker-config', 'Conferencing enabled - notetaker can now be configured');
    } else if (wasConfigured && !isConfigured) {
      debug('nylas-notetaker-config', 'Conferencing disabled - clearing notetaker settings');
    }

    // If conferencing is disabled/cleared, automatically unset notetaker
    if (!newValue || !newValue.provider) {
      this.enabled = false;
      this.showUiConsentMessage = false;
      this.notetakerName = '';
      this.videoRecording = false;
      this.audioRecording = false;
      this.transcription = false;
      this.summary = false;
      this.summaryCustomInstructions = '';
      this.actionItems = false;
      this.actionItemsCustomInstructions = '';
      this.validateAndEmit(true);
    } else if (isConfigured) {
      // Conferencing is now configured - trigger validation to ensure state is consistent
      this.validateAndEmit(false);
    }
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  connectedCallback() {
    debug('nylas-notetaker-config', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    debug('nylas-notetaker-config', 'componentWillLoad');
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-notetaker-config', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    }
    // Check if conferencing is configured
    if (!this.conferencing || !this.conferencing.provider) {
      this.conferencingChangedHandler(this.conferencing);
    }
  }

  disconnectedCallback() {
    debug('nylas-notetaker-config', 'disconnectedCallback');
  }

  /**
   * Public method to programmatically update conferencing configuration
   */
  @Method()
  async updateConferencing(conferencing: Conference | undefined): Promise<void> {
    debug('nylas-notetaker-config', 'updateConferencing', conferencing);
    this.conferencing = conferencing;
    // The @Watch decorator will automatically trigger conferencingChangedHandler
  }

  /**
   * Public method to programmatically set notetaker enabled state
   * @returns Object with success status and optional error message
   */
  @Method()
  async setNotetakerEnabled(enabled: boolean): Promise<{ success: boolean; error?: string }> {
    if (!this.isConferencingConfigured) {
      const error = 'Cannot enable notetaker: conferencing is not configured';
      debug('nylas-notetaker-config:', error);
      this.notetakerError.emit({
        error,
        reason: 'conferencing_not_configured',
        context: { enabled },
      });
      return { success: false, error };
    }

    this.enabled = enabled;

    // When enabling Notetaker, default enable video, audio recording, and transcription
    if (enabled) {
      this.videoRecording = true;
      this.audioRecording = true;
      this.transcription = true;
    }

    // Validate and emit - validation errors are stored in state, not returned
    this.validateAndEmit(true, true);
    // Return success: true if the operation succeeded (conferencing was configured)
    // Validation errors are available via getNotetakerState().validationError
    return { success: true };
  }

  /**
   * Public method to programmatically set a notetaker option
   * @param option - The option name (e.g., 'notetakerName', 'videoRecording', 'audioRecording', etc.)
   * @param value - The value to set (boolean for checkboxes, string for inputs)
   * @returns Object with success status and optional error message
   */
  @Method()
  async setNotetakerOption(option: string, value: boolean | string): Promise<{ success: boolean; error?: string }> {
    // notetakerName can always be set, regardless of conferencing status
    if (option === 'notetakerName') {
      if (typeof value !== 'string') {
        return { success: false, error: 'notetakerName must be a string' };
      }
      this.notetakerName = value;
      // Name can be set even if notetaker is not enabled or conferencing is not configured
      // Validate and emit but always return success
      this.validateAndEmit(true, true);
      return { success: true };
    }

    // For all other options, conferencing must be configured
    if (!this.isConferencingConfigured) {
      const error = `Cannot set ${option}: conferencing is not configured`;
      debug('nylas-notetaker-config:', error);
      this.notetakerError.emit({
        error,
        reason: 'conferencing_not_configured',
        context: { option, value },
      });
      return { success: false, error };
    }

    switch (option) {
      case 'enabled':
        return this.setNotetakerEnabled(value === true);
      case 'showUiConsentMessage':
        if (typeof value !== 'boolean') {
          return { success: false, error: 'showUiConsentMessage must be a boolean' };
        }
        this.showUiConsentMessage = value;
        break;
      case 'videoRecording':
        if (typeof value !== 'boolean') {
          return { success: false, error: 'videoRecording must be a boolean' };
        }
        this.videoRecording = value;
        break;
      case 'audioRecording':
        if (typeof value !== 'boolean') {
          return { success: false, error: 'audioRecording must be a boolean' };
        }
        this.audioRecording = value;
        // When disabling audio recording, also disable transcription, summary, and action items
        if (!value) {
          this.transcription = false;
          this.summary = false;
          this.actionItems = false;
          this.summaryCustomInstructions = '';
          this.actionItemsCustomInstructions = '';
        }
        break;
      case 'transcription':
        if (typeof value !== 'boolean') {
          return { success: false, error: 'transcription must be a boolean' };
        }
        if (value && !this.audioRecording) {
          const error = 'Cannot enable transcription: audio recording must be enabled first';
          debug('nylas-notetaker-config:', error);
          this.notetakerError.emit({
            error,
            reason: 'audio_recording_required',
            context: { option, value },
          });
          return { success: false, error };
        }
        this.transcription = value;
        // When disabling transcription, also disable summary and action items
        if (!value) {
          this.summary = false;
          this.actionItems = false;
          this.summaryCustomInstructions = '';
          this.actionItemsCustomInstructions = '';
        }
        break;
      case 'summary':
        if (typeof value !== 'boolean') {
          return { success: false, error: 'summary must be a boolean' };
        }
        if (value && !this.transcription) {
          const error = 'Cannot enable summary: transcription must be enabled first';
          debug('nylas-notetaker-config:', error);
          this.notetakerError.emit({
            error,
            reason: 'transcription_required',
            context: { option, value },
          });
          return { success: false, error };
        }
        this.summary = value;
        if (!value) {
          this.summaryCustomInstructions = '';
        }
        break;
      case 'actionItems':
        if (typeof value !== 'boolean') {
          return { success: false, error: 'actionItems must be a boolean' };
        }
        if (value && !this.transcription) {
          const error = 'Cannot enable action items: transcription must be enabled first';
          debug('nylas-notetaker-config:', error);
          this.notetakerError.emit({
            error,
            reason: 'transcription_required',
            context: { option, value },
          });
          return { success: false, error };
        }
        this.actionItems = value;
        if (!value) {
          this.actionItemsCustomInstructions = '';
        }
        break;
      case 'summaryCustomInstructions':
        if (typeof value !== 'string') {
          return { success: false, error: 'summaryCustomInstructions must be a string' };
        }
        this.summaryCustomInstructions = value;
        break;
      case 'actionItemsCustomInstructions':
        if (typeof value !== 'string') {
          return { success: false, error: 'actionItemsCustomInstructions must be a string' };
        }
        this.actionItemsCustomInstructions = value;
        break;
      default:
        return { success: false, error: `Unknown option: ${option}` };
    }

    // Validate and emit - validation errors are stored in state, not returned
    this.validateAndEmit(true, true);
    // Return success: true if the operation succeeded
    // Validation errors are available via getNotetakerState().validationError
    return { success: true };
  }

  /**
   * Public method to get current notetaker state
   * @returns Object containing all current state values
   */
  @Method()
  async getNotetakerState(): Promise<NotetakerState> {
    return {
      enabled: this.enabled,
      notetakerName: this.notetakerName,
      showUiConsentMessage: this.showUiConsentMessage,
      videoRecording: this.videoRecording,
      audioRecording: this.audioRecording,
      transcription: this.transcription,
      summary: this.summary,
      actionItems: this.actionItems,
      summaryCustomInstructions: this.summaryCustomInstructions,
      actionItemsCustomInstructions: this.actionItemsCustomInstructions,
      isConferencingConfigured: this.isConferencingConfigured,
      conferencing: this.conferencing,
      validationError: this.validationError,
      summaryInstructionsError: this.summaryInstructionsError,
      actionItemsInstructionsError: this.actionItemsInstructionsError,
    };
  }

  /**
   * Check if conferencing is configured
   */
  get isConferencingConfigured(): boolean {
    return !!(this.conferencing && this.conferencing.provider);
  }

  /**
   * Check if at least one setting is enabled
   */
  get hasAtLeastOneSetting(): boolean {
    return this.videoRecording || this.audioRecording || this.transcription || this.summary || this.actionItems;
  }

  /**
   * Validate the notetaker configuration
   */
  validate(): boolean {
    this.validationError = '';
    this.summaryInstructionsError = '';
    this.actionItemsInstructionsError = '';

    // If notetaker is enabled, name is required
    if (this.enabled && !this.notetakerName.trim()) {
      this.validationError = i18next.t('nylasNotetakerConfig.validation.nameRequired') || 'Notetaker name is required when recording is enabled.';
      return false;
    }

    // If notetaker is enabled, at least one setting must be enabled
    if (this.enabled && !this.hasAtLeastOneSetting) {
      this.validationError = i18next.t('nylasNotetakerConfig.validation.atLeastOneSettingRequired') || 'At least one recording or AI feature must be enabled.';
      return false;
    }

    // Validate that transcription requires audio recording
    if (this.transcription && !this.audioRecording) {
      this.validationError = i18next.t('nylasNotetakerConfig.validation.audioRecordingRequired') || 'Audio recording must be enabled to use transcription.';
      return false;
    }

    // Validate that summary or action items require transcription
    if ((this.summary || this.actionItems) && !this.transcription) {
      this.validationError = i18next.t('nylasNotetakerConfig.validation.transcriptionRequired') || 'Transcription must be enabled to use AI summary or action items features.';
      return false;
    }

    // Validate summary custom instructions length
    if (this.summary && this.summaryCustomInstructions.length > this.MAX_CUSTOM_INSTRUCTIONS_LENGTH) {
      this.summaryInstructionsError =
        i18next.t('nylasNotetakerConfig.validation.customInstructionsMaxLength', { max: this.MAX_CUSTOM_INSTRUCTIONS_LENGTH }) ||
        `Additional prompt cannot exceed ${this.MAX_CUSTOM_INSTRUCTIONS_LENGTH} characters.`;
      this.validationError = this.summaryInstructionsError;
      return false;
    }

    // Validate action items custom instructions length
    if (this.actionItems && this.actionItemsCustomInstructions.length > this.MAX_CUSTOM_INSTRUCTIONS_LENGTH) {
      this.actionItemsInstructionsError =
        i18next.t('nylasNotetakerConfig.validation.customInstructionsMaxLength', { max: this.MAX_CUSTOM_INSTRUCTIONS_LENGTH }) ||
        `Additional prompt cannot exceed ${this.MAX_CUSTOM_INSTRUCTIONS_LENGTH} characters.`;
      this.validationError = this.actionItemsInstructionsError;
      return false;
    }

    return true;
  }

  /**
   * Build the Notetaker object from current state
   */
  buildNotetakerObject(): Notetaker | null {
    // If conferencing is not configured, return { enabled: false } to preserve the notetaker payload
    if (!this.isConferencingConfigured) {
      return { enabled: false };
    }

    // If not enabled but conferencing is configured, explicitly return { enabled: false }
    // This ensures the API receives the disabled state instead of omitting the field
    if (!this.enabled) {
      return { enabled: false };
    }

    const meetingSettings: any = {};

    // Always set boolean values explicitly to ensure unchecked options are sent as false
    meetingSettings.video_recording = this.videoRecording;
    meetingSettings.audio_recording = this.audioRecording;
    meetingSettings.transcription = this.transcription;
    meetingSettings.summary = this.summary;
    meetingSettings.action_items = this.actionItems;

    // Add custom instructions only when the feature is enabled and instructions exist
    if (this.summary && this.summaryCustomInstructions.trim()) {
      meetingSettings.summary_settings = {
        custom_instructions: this.summaryCustomInstructions.trim(),
      };
    }
    if (this.actionItems && this.actionItemsCustomInstructions.trim()) {
      meetingSettings.action_items_settings = {
        custom_instructions: this.actionItemsCustomInstructions.trim(),
      };
    }

    const notetaker: Notetaker = {
      enabled: true,
      show_ui_consent_message: this.showUiConsentMessage,
      notetaker_name: this.notetakerName.trim(),
    };

    if (Object.keys(meetingSettings).length > 0) {
      notetaker.meeting_settings = meetingSettings;
    }

    return notetaker;
  }

  /**
   * Validate and emit the value
   */
  validateAndEmit(valueChanged: boolean = true, forceRerender: boolean = false): boolean {
    const isValid = this.validate();

    if (isValid) {
      const notetaker = this.buildNotetakerObject();
      const value = notetaker ? JSON.stringify(notetaker) : JSON.stringify(null);

      if (valueChanged) {
        this.valueChanged.emit({
          value,
          name: this.name,
        });
      }

      if (this.isInternalsAvailable) {
        this.internals.setFormValue(value, this.name);
        this.internals.setValidity({});
      }

      // Force update if requested (useful for programmatic state changes)
      if (forceRerender) {
        // Trigger re-render by accessing a state property
        forceUpdate(this);
      }
    } else {
      if (this.isInternalsAvailable) {
        this.internals.setFormValue('', this.name);
        this.internals.setValidity(
          {
            customError: true,
          },
          this.validationError,
        );
      }
    }

    return isValid;
  }

  get isInternalsAvailable() {
    return this.internals !== undefined && typeof this.internals.setFormValue === 'function';
  }

  @Listen('nylasFormCheckboxToggled', { target: 'document' })
  nylasFormCheckboxToggledHandler(event: CustomEvent<{ checked: boolean; name: string }>) {
    debug('nylas-notetaker-config', 'nylasFormCheckboxToggledHandler', event.detail);
    const { name, checked } = event.detail;

    // Only handle events from this component's children
    if (!name.startsWith('notetaker-')) {
      return;
    }

    // Don't allow any changes if conferencing is not configured
    if (!this.isConferencingConfigured) {
      const errorMessage = `Cannot ${checked ? 'enable' : 'disable'} ${name}: conferencing is not configured`;
      debug('nylas-notetaker-config:', errorMessage);
      this.notetakerError.emit({
        error: errorMessage,
        reason: 'conferencing_not_configured',
        context: { checkboxName: name, checked },
      });
      return;
    }

    switch (name) {
      case 'notetaker-enabled':
        this.enabled = checked;
        // When enabling Notetaker, default enable video, audio recording, and transcription
        if (checked) {
          this.videoRecording = true;
          this.audioRecording = true;
          this.transcription = true;
        }
        break;
      case 'notetaker-show-consent':
        this.showUiConsentMessage = checked;
        break;
      case 'notetaker-video':
        this.videoRecording = checked;
        break;
      case 'notetaker-audio':
        this.audioRecording = checked;
        // When disabling audio recording, also disable transcription, summary, and action items
        if (!checked) {
          this.transcription = false;
          this.summary = false;
          this.actionItems = false;
          // Clear custom instructions when disabling audio recording
          this.summaryCustomInstructions = '';
          this.actionItemsCustomInstructions = '';
        }
        break;
      case 'notetaker-transcription':
        // Only allow enabling transcription if audio recording is enabled
        if (checked && !this.audioRecording) {
          const errorMessage = 'Cannot enable transcription: audio recording must be enabled first';
          debug('nylas-notetaker-config:', errorMessage);
          this.notetakerError.emit({
            error: errorMessage,
            reason: 'audio_recording_required',
            context: { checkboxName: name, checked },
          });
          return;
        }
        this.transcription = checked;
        // When disabling transcription, also disable summary and action items
        if (!checked) {
          this.summary = false;
          this.actionItems = false;
          // Clear custom instructions when disabling transcription
          this.summaryCustomInstructions = '';
          this.actionItemsCustomInstructions = '';
        }
        break;
      case 'notetaker-summary':
        // Only allow enabling summary if transcription is enabled
        if (checked && !this.transcription) {
          const errorMessage = 'Cannot enable summary: transcription must be enabled first';
          debug('nylas-notetaker-config:', errorMessage);
          this.notetakerError.emit({
            error: errorMessage,
            reason: 'transcription_required',
            context: { checkboxName: name, checked },
          });
          return;
        }
        this.summary = checked;
        // Clear custom instructions when disabling summary
        if (!checked) {
          this.summaryCustomInstructions = '';
        }
        break;
      case 'notetaker-action-items':
        // Only allow enabling action items if transcription is enabled
        if (checked && !this.transcription) {
          const errorMessage = 'Cannot enable action items: transcription must be enabled first';
          debug('nylas-notetaker-config:', errorMessage);
          this.notetakerError.emit({
            error: errorMessage,
            reason: 'transcription_required',
            context: { checkboxName: name, checked },
          });
          return;
        }
        this.actionItems = checked;
        // Clear custom instructions when disabling action items
        if (!checked) {
          this.actionItemsCustomInstructions = '';
        }
        break;
    }
    this.validateAndEmit();
  }

  @Listen('nylasFormInputChanged', { target: 'document' })
  nylasFormInputChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    debug('nylas-notetaker-config', 'nylasFormInputChangedHandler', event.detail);
    const { name, value } = event.detail;

    // Only handle events from this component's children
    if (!name.startsWith('notetaker-')) {
      return;
    }

    // notetaker-name can always be set, regardless of conferencing status
    if (name === 'notetaker-name') {
      this.notetakerName = value;
      this.validateAndEmit();
      return;
    }

    // For all other inputs, conferencing must be configured
    if (!this.isConferencingConfigured) {
      const errorMessage = `Cannot set ${name}: conferencing is not configured`;
      debug('nylas-notetaker-config:', errorMessage);
      this.notetakerError.emit({
        error: errorMessage,
        reason: 'conferencing_not_configured',
        context: { inputName: name, value },
      });
      return;
    }

    switch (name) {
      case 'notetaker-summary-instructions':
        this.summaryCustomInstructions = value;
        // Clear error when user starts typing
        if (this.summaryInstructionsError && value.length <= this.MAX_CUSTOM_INSTRUCTIONS_LENGTH) {
          this.summaryInstructionsError = '';
        }
        break;
      case 'notetaker-action-items-instructions':
        this.actionItemsCustomInstructions = value;
        // Clear error when user starts typing
        if (this.actionItemsInstructionsError && value.length <= this.MAX_CUSTOM_INSTRUCTIONS_LENGTH) {
          this.actionItemsInstructionsError = '';
        }
        break;
    }
    this.validateAndEmit();
  }

  @RegisterComponent<NylasNotetakerConfig, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-notetaker-config',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {},
    fireRegisterEvent: true,
  })
  render() {
    const isDisabled = !this.isConferencingConfigured;

    return (
      <Host>
        <div class="nylas-notetaker-config" part="nntc">
          <div class="header" part="nntc__header">
            <h3>{i18next.t('nylasNotetakerConfig.headerTitle') || 'Notetaker settings'}</h3>
            <p>{i18next.t('nylasNotetakerConfig.headerSubTitle') || 'Configure AI-powered meeting recording and note-taking.'}</p>
          </div>

          <div class="content" part="nntc__content">
            {isDisabled && (
              <div class="info-message" role="status">
                {i18next.t('nylasNotetakerConfig.info.conferencingRequired') ||
                  'Conferencing must be enabled to use Notetaker. Please configure conferencing in the Event Info tab first.'}
              </div>
            )}

            {/* Primary enable toggle */}
            <div class={`primary-toggle ${isDisabled ? 'checkbox-disabled' : ''}`} part="nntc__primary-toggle">
              <checkbox-component
                name="notetaker-enabled"
                label={i18next.t('nylasNotetakerConfig.basicSettings.recordMeeting.label') || 'Enable Notetaker for this event'}
                checked={this.enabled}
                readOnly={isDisabled}
                themeConfig={this.themeConfig}
              >
                <span slot="label" class="label">
                  <p class="label-contents">{i18next.t('nylasNotetakerConfig.basicSettings.recordMeeting.label') || 'Enable Notetaker for this event'}</p>
                  <tooltip-component>
                    <info-icon slot="tooltip-icon" />
                    <span slot="tooltip-content">
                      {i18next.t('nylasNotetakerConfig.basicSettings.recordMeeting.tooltip.desc') ||
                        'When enabled, Notetaker will automatically join booked meetings to record and generate AI-powered notes.'}
                    </span>
                  </tooltip-component>
                </span>
              </checkbox-component>
            </div>

            {/* Basic settings section */}
            <div class="section" part="nntc__section">
              <h4 class="section-title" part="nntc__section-title">
                {i18next.t('nylasNotetakerConfig.basicSettings.title') || 'Basic settings'}
              </h4>
              <div class="section-content">
                <div class="input-container" part="nntc__input">
                  <input-component
                    name="notetaker-name"
                    label={i18next.t('nylasNotetakerConfig.basicSettings.name.label') || 'Notetaker display name'}
                    defaultValue={this.notetakerName}
                    required={this.enabled}
                    readOnly={isDisabled || !this.enabled}
                    placeholder={i18next.t('nylasNotetakerConfig.basicSettings.name.placeholder') || 'e.g., Nylas Notetaker'}
                    themeConfig={this.themeConfig}
                  />
                  <p class="input-help-text">
                    {i18next.t('nylasNotetakerConfig.basicSettings.name.tooltip.desc') || 'The name that will appear for the Notetaker bot when it joins your meetings.'}
                  </p>
                </div>
                <div class={`checkbox-container ${isDisabled || !this.enabled ? 'checkbox-disabled' : ''}`} part="nntc__checkbox">
                  <checkbox-component
                    name="notetaker-show-consent"
                    label={i18next.t('nylasNotetakerConfig.basicSettings.collectConsent.label') || 'Collect user consent during booking'}
                    checked={this.showUiConsentMessage}
                    readOnly={isDisabled || !this.enabled}
                    themeConfig={this.themeConfig}
                  >
                    <span slot="label" class="label">
                      <p class="label-contents">{i18next.t('nylasNotetakerConfig.basicSettings.collectConsent.label') || 'Collect user consent during booking'}</p>
                      <tooltip-component>
                        <info-icon slot="tooltip-icon" />
                        <span slot="tooltip-content">
                          {i18next.t('nylasNotetakerConfig.basicSettings.collectConsent.tooltip.desc') ||
                            'Display a consent message to meeting participants during the booking process, informing them that the meeting will be recorded.'}
                        </span>
                      </tooltip-component>
                    </span>
                  </checkbox-component>
                </div>
              </div>
            </div>

            {/* Recording section */}
            <div class="section" part="nntc__section">
              <h4 class="section-title" part="nntc__section-title">
                {i18next.t('nylasNotetakerConfig.recording.title') || 'Recording options'}
              </h4>
              <p class="section-description" part="nntc__section-description">
                {i18next.t('nylasNotetakerConfig.recording.description') || 'Select what you want to capture from your meetings.'}
              </p>
              <div class="section-content">
                <div class={`checkbox-container ${isDisabled || !this.enabled ? 'checkbox-disabled' : ''}`} part="nntc__checkbox">
                  <checkbox-component
                    name="notetaker-video"
                    label={i18next.t('nylasNotetakerConfig.recording.video.label') || 'Video recording'}
                    checked={this.videoRecording}
                    readOnly={isDisabled || !this.enabled}
                    themeConfig={this.themeConfig}
                  >
                    <span slot="label" class="label">
                      <p class="label-contents">{i18next.t('nylasNotetakerConfig.recording.video.label') || 'Video recording'}</p>
                      <tooltip-component>
                        <info-icon slot="tooltip-icon" />
                        <span slot="tooltip-content">
                          {i18next.t('nylasNotetakerConfig.recording.video.tooltip.desc') || 'Record video from the meeting. Video files will be available after the meeting ends.'}
                        </span>
                      </tooltip-component>
                    </span>
                  </checkbox-component>
                </div>
                <div class={`checkbox-container ${isDisabled || !this.enabled ? 'checkbox-disabled' : ''}`} part="nntc__checkbox">
                  <checkbox-component
                    name="notetaker-audio"
                    label={i18next.t('nylasNotetakerConfig.recording.audio.label') || 'Audio recording'}
                    checked={this.audioRecording}
                    readOnly={isDisabled || !this.enabled}
                    themeConfig={this.themeConfig}
                  >
                    <span slot="label" class="label">
                      <p class="label-contents">{i18next.t('nylasNotetakerConfig.recording.audio.label') || 'Audio recording'}</p>
                      <tooltip-component>
                        <info-icon slot="tooltip-icon" />
                        <span slot="tooltip-content">
                          {i18next.t('nylasNotetakerConfig.recording.audio.tooltip.desc') ||
                            'Record audio from the meeting. Audio files will be available after the meeting ends. Required for transcription and AI features.'}
                        </span>
                      </tooltip-component>
                    </span>
                  </checkbox-component>
                </div>
                <div class={`checkbox-container ${isDisabled || !this.enabled || !this.audioRecording ? 'checkbox-disabled' : ''}`} part="nntc__checkbox">
                  <checkbox-component
                    name="notetaker-transcription"
                    label={i18next.t('nylasNotetakerConfig.recording.transcription.label') || 'Transcription'}
                    checked={this.transcription}
                    readOnly={isDisabled || !this.enabled || !this.audioRecording}
                    themeConfig={this.themeConfig}
                  >
                    <span slot="label" class="label">
                      <p class="label-contents">{i18next.t('nylasNotetakerConfig.recording.transcription.label') || 'Transcription'}</p>
                      <tooltip-component>
                        <info-icon slot="tooltip-icon" />
                        <span slot="tooltip-content">
                          {i18next.t('nylasNotetakerConfig.recording.transcription.tooltip.desc') ||
                            'Generate a text transcript of the meeting conversation. Requires audio recording. Required for AI features.'}
                        </span>
                      </tooltip-component>
                    </span>
                  </checkbox-component>
                </div>
              </div>
            </div>

            {/* AI features section */}
            <div class="section" part="nntc__section">
              <h4 class="section-title" part="nntc__section-title">
                {i18next.t('nylasNotetakerConfig.aiFeatures.title') || 'AI-powered features'}
              </h4>
              <p class="section-description" part="nntc__section-description">
                {i18next.t('nylasNotetakerConfig.aiFeatures.description') || 'Enhance your meetings with AI-generated summaries and action items.'}
              </p>
              <div class="section-content">
                <div class={`checkbox-container ${isDisabled || !this.enabled || !this.transcription ? 'checkbox-disabled' : ''}`} part="nntc__checkbox">
                  <checkbox-component
                    name="notetaker-summary"
                    label={i18next.t('nylasNotetakerConfig.aiFeatures.summary.label') || 'AI summary'}
                    checked={this.summary}
                    readOnly={isDisabled || !this.enabled || !this.transcription}
                    themeConfig={this.themeConfig}
                  >
                    <span slot="label" class="label">
                      <p class="label-contents">{i18next.t('nylasNotetakerConfig.aiFeatures.summary.label') || 'AI summary'}</p>
                      <tooltip-component>
                        <info-icon slot="tooltip-icon" />
                        <span slot="tooltip-content">
                          {i18next.t('nylasNotetakerConfig.aiFeatures.summary.tooltip.desc') ||
                            'Generate an AI-powered summary of the meeting, including key discussion points and decisions. Requires audio recording and transcription to be enabled.'}
                        </span>
                      </tooltip-component>
                    </span>
                  </checkbox-component>
                </div>
                {this.summary && (
                  <div class="textarea-container" part="nntc__textarea">
                    <textarea-component
                      name="notetaker-summary-instructions"
                      label={i18next.t('nylasNotetakerConfig.aiFeatures.summary.instructionsLabel') || 'Additional prompt'}
                      defaultValue={this.summaryCustomInstructions}
                      placeholder={i18next.t('nylasNotetakerConfig.aiFeatures.summary.instructionsPlaceholder') || 'Enter additional context for AI summary'}
                      readOnly={isDisabled || !this.enabled}
                      maxLength={this.MAX_CUSTOM_INSTRUCTIONS_LENGTH}
                      themeConfig={this.themeConfig}
                    />
                    {this.summaryInstructionsError && (
                      <div class="field-error-message" role="alert">
                        {this.summaryInstructionsError}
                      </div>
                    )}
                    {!this.summaryInstructionsError && (
                      <div class="character-count">
                        {this.summaryCustomInstructions.length} / {this.MAX_CUSTOM_INSTRUCTIONS_LENGTH}
                      </div>
                    )}
                  </div>
                )}
                <div class={`checkbox-container ${isDisabled || !this.enabled || !this.transcription ? 'checkbox-disabled' : ''}`} part="nntc__checkbox">
                  <checkbox-component
                    name="notetaker-action-items"
                    label={i18next.t('nylasNotetakerConfig.aiFeatures.actionItems.label') || 'AI action items'}
                    checked={this.actionItems}
                    readOnly={isDisabled || !this.enabled || !this.transcription}
                    themeConfig={this.themeConfig}
                  >
                    <span slot="label" class="label">
                      <p class="label-contents">{i18next.t('nylasNotetakerConfig.aiFeatures.actionItems.label') || 'AI action items'}</p>
                      <tooltip-component>
                        <info-icon slot="tooltip-icon" />
                        <span slot="tooltip-content">
                          {i18next.t('nylasNotetakerConfig.aiFeatures.actionItems.tooltip.desc') ||
                            'Automatically extract action items, tasks, and to-dos mentioned during the meeting. Requires audio recording and transcription to be enabled.'}
                        </span>
                      </tooltip-component>
                    </span>
                  </checkbox-component>
                </div>
                {this.actionItems && (
                  <div class="textarea-container" part="nntc__textarea">
                    <textarea-component
                      name="notetaker-action-items-instructions"
                      label={i18next.t('nylasNotetakerConfig.aiFeatures.actionItems.instructionsLabel') || 'Additional prompt'}
                      defaultValue={this.actionItemsCustomInstructions}
                      placeholder={i18next.t('nylasNotetakerConfig.aiFeatures.actionItems.instructionsPlaceholder') || 'Enter additional context for AI action items'}
                      readOnly={isDisabled || !this.enabled}
                      maxLength={this.MAX_CUSTOM_INSTRUCTIONS_LENGTH}
                      themeConfig={this.themeConfig}
                    />
                    {this.actionItemsInstructionsError && (
                      <div class="field-error-message" role="alert">
                        {this.actionItemsInstructionsError}
                      </div>
                    )}
                    {!this.actionItemsInstructionsError && (
                      <div class="character-count">
                        {this.actionItemsCustomInstructions.length} / {this.MAX_CUSTOM_INSTRUCTIONS_LENGTH}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {this.validationError && (
              <div class="error-message" role="alert">
                {this.validationError}
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
